import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Stack,
} from '@mui/material'
import { User } from '../../types/user'
import { UserService } from '../../services/userService'
import { useAuth } from '../../contexts/AuthContext'

interface UserCreateModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (user: User, message: string) => void
}

interface UserCreateForm {
  name: string
  email: string
  department: string
  position: string
  role: string
  preferredLanguage: 'JA' | 'EN'
}

export const UserCreateModal: React.FC<UserCreateModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation('user')
  const { user: currentUser } = useAuth()
  const [form, setForm] = useState<UserCreateForm>({
    name: '',
    email: '',
    department: '',
    position: '',
    role: 'USER',
    preferredLanguage: 'JA',
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleClose = () => {
    if (!saving) {
      setForm({
        name: '',
        email: '',
        department: '',
        position: '',
        role: 'USER',
        preferredLanguage: 'JA',
      })
      setErrors({})
      onClose()
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!form.name.trim()) {
      newErrors.name = t('form.validation.nameRequired')
    }

    if (!form.email.trim()) {
      newErrors.email = t('form.validation.emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = t('form.validation.emailInvalid')
    }

    // Password is auto-generated, so validation removed

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Password auto-generation is handled server-side

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      setSaving(true)

      const userData = {
        name: form.name,
        email: form.email,
        department: form.department || undefined,
        position: form.position || undefined,
        role: form.role,
        preferredLanguage: form.preferredLanguage,
        // isActive is automatically set to false on backend until email delivery succeeds
      }

      const createdUser = await UserService.createUser(userData)
      onSuccess(createdUser, t('messages.createSuccess'))
      handleClose()
    } catch (error) {
      console.error('User creation error:', error)
      setErrors({ general: t('messages.loadError') })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = (field: keyof UserCreateForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={saving}>
      <DialogTitle>{t('create.title')}</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {errors.general && <Alert severity="error">{errors.general}</Alert>}

          {/* 基本情報 */}
          <Stack spacing={2}>
            <Typography variant="h6">{t('common:sections.basicInfo')}</Typography>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label={t('form.name')}
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                placeholder={t('form.namePlaceholder')}
              />
              <TextField
                fullWidth
                label={t('form.email')}
                type="email"
                value={form.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                required
                placeholder={t('form.emailPlaceholder')}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label={t('form.department')}
                value={form.department}
                onChange={(e) => handleFormChange('department', e.target.value)}
                placeholder={t('form.departmentPlaceholder')}
              />
              <TextField
                fullWidth
                label={t('form.position')}
                value={form.position}
                onChange={(e) => handleFormChange('position', e.target.value)}
                placeholder={t('form.positionPlaceholder')}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>{t('form.role')}</InputLabel>
                <Select
                  value={form.role}
                  label={t('form.role')}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                >
                  <MenuItem value="USER">{t('common:roles.user')}</MenuItem>
                  <MenuItem value="CORPORATE_ADMIN">{t('common:roles.admin')}</MenuItem>
                  {currentUser?.role === 'SUPER_ADMIN' && (
                    <MenuItem value="SUPER_ADMIN">{t('common:roles.superAdmin')}</MenuItem>
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('common:language.label')}</InputLabel>
                <Select
                  value={form.preferredLanguage}
                  label={t('common:language.label')}
                  onChange={(e) => handleFormChange('preferredLanguage', e.target.value)}
                >
                  <MenuItem value="JA">{t('common:language.ja')}</MenuItem>
                  <MenuItem value="EN">{t('common:language.en')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {/* Notices */}
          <Alert severity="info">
            <Typography variant="body2">
              ・{t('form.notices.firstLoginNotice')}
              <br />・{t('form.notices.adminRoleNotice')}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('dialogs.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? t('common:buttons.creating') : t('common:buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
